/**
 * Story 123.2 — capability → remote module URL registry.
 *
 * Each entry maps a capability key (from the module catalogue, story
 * 121.1) to the URL of a remote ESM bundle. The shell consults
 * `/me/capabilities` (story 121.4) to know which keys are effective
 * for the current tenant; for every effective key with an entry here,
 * the shell mounts the remote.
 *
 * **Today** (pre-GEDU cutover) this map is empty by design — the
 * scaffolding exists, no real remote is published yet. The pilot
 * placeholder remote (story 123.4) lives at the URL noted below as
 * a smoke-test target; flip the PLACEHOLDER_REMOTE_ENABLED flag and
 * point the URL at the running placeholder to verify the runtime
 * end-to-end. GEDU's federated remote slots in at the
 * `gedu.qualification` / `gedu.cobranza` keys when 125.3 lands.
 *
 * The registry is intentionally STATIC for now (compile-time JSON);
 * a future story can promote it to a backend-served manifest if the
 * mapping needs to be per-tenant or per-deployment without rebuilds.
 */

export interface RemoteEntry {
  /**
   * URL that resolves to an ESM module conforming to
   * [RemoteModuleExport]. Can be relative (served by Vite dev) or
   * absolute (CDN / S3 in prod).
   */
  url: string;
  /**
   * Nav label key for the remote's top-level entry. The shell uses
   * `t(navLabelKey)` to render it.
   */
  navLabelKey?: string;
}

/**
 * Static, compile-time entries. Empty by design — Kairos core is
 * vertical-agnostic and must NOT hard-code any plugin (e.g. Educatio)
 * here. Real remotes are injected per-deployment via the
 * `VITE_REMOTE_MODULES` build env (see {@link parseRemoteModulesEnv}),
 * so registering a plugin's federated remote is a deploy-time config
 * change, not a code change in the shell.
 */
const STATIC_REMOTES: Record<string, RemoteEntry> = {};

/**
 * Parses the `VITE_REMOTE_MODULES` build env into registry entries
 * (story 123.5). The env is a JSON object mapping a capability key to
 * either a URL string or a {url, navLabelKey} object, e.g.:
 *
 *   VITE_REMOTE_MODULES='{"educatio.console":"https://educatio.demo.lopettia.ai/remote.js"}'
 *
 * This keeps every vertical literal in deploy config/data — never in
 * the shell's source. Invalid/blank JSON yields an empty map (the
 * shell never breaks over a malformed env).
 */
export function parseRemoteModulesEnv(raw: string | undefined): Record<string, RemoteEntry> {
  if (!raw || !raw.trim()) return {};
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null) return {};
  const out: Record<string, RemoteEntry> = {};
  for (const [key, val] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof val === 'string') {
      if (val.trim()) out[key] = { url: val };
    } else if (val && typeof val === 'object' && typeof (val as RemoteEntry).url === 'string') {
      const e = val as RemoteEntry;
      if (e.url.trim()) out[key] = { url: e.url, navLabelKey: e.navLabelKey };
    }
  }
  return out;
}

/**
 * The effective registry: static entries overlaid with the
 * per-deployment remotes from `VITE_REMOTE_MODULES`. Env entries win on
 * key collision (a deployment can override a built-in mapping).
 */
export const REMOTE_REGISTRY: Record<string, RemoteEntry> = {
  ...STATIC_REMOTES,
  ...parseRemoteModulesEnv(import.meta.env?.VITE_REMOTE_MODULES as string | undefined),
};

/**
 * Pilot placeholder remote (story 123.4). Wired but disabled by
 * default; enabling it requires a running placeholder remote server
 * at this URL and adding an entry to REMOTE_REGISTRY for whatever
 * capability key the pilot exercises (recommended: a test-only key
 * like `pilot.placeholder` granted only to the smoke-test tenant).
 */
export const PLACEHOLDER_REMOTE_URL = 'http://localhost:5174/remote.js';
